import { Organization } from '@models/Organization';
import { useEffect, useState } from 'react';
import { PilotOnboardingService, PilotOrganizationSetup, PilotSourceOption } from '@services/PilotOnboardingService';
import { UsersService } from '@services/UsersService';

export interface PilotOnboardingSubmissionResult {
    kind: 'success' | 'error';
    message?: string;
    errorMessage?: string;
}

interface UsePilotOnboardingStartModalViewModelProps {
    currentOrganization?: Organization;
    isOpen?: boolean;
    pilotOnboardingService: PilotOnboardingService;
    usersService: UsersService;
}

export const getPilotOnboardingErrorMessage = (error: unknown) => {
    if (!(error instanceof Error)) {
        return 'No se pudo crear el piloto. Intenta nuevamente.'.localized();
    }

    const rawMessage = error.message?.trim();
    if (!rawMessage) {
        return 'No se pudo crear el piloto. Intenta nuevamente.'.localized();
    }

    try {
        const parsed = JSON.parse(rawMessage);
        if (parsed?.message) {
            return parsed.message;
        }
        if (parsed?.error) {
            return parsed.error;
        }
    } catch {}

    return rawMessage;
};

export const canSubmitPilotOnboarding = (
    organizationID: string,
    countryCode: string,
    name: string,
    adminEmail: string,
    buildingName: string,
    latitude: string,
    longitude: string,
    isSubmitting: boolean,
    organizationSetup: PilotOrganizationSetup = 'presenteeism',
    pilotSource: PilotSourceOption | '' = 'other',
) => {
    const includesQontactPoint = organizationSetup !== 'payslips';
    const hasQontactPoint = includesQontactPoint && Boolean(buildingName.trim());
    const hasRequiredCoordinates = hasQontactPoint
        ? Boolean(latitude.trim() && longitude.trim())
        : true;
    const hasValidAdminEmail = !adminEmail.trim() || /\S+@\S+\.\S+/.test(adminEmail.trim());

    return Boolean(
        organizationID.trim() &&
        countryCode.trim() &&
        name.trim() &&
        pilotSource &&
        hasValidAdminEmail &&
        hasRequiredCoordinates &&
        !isSubmitting
    );
};

export const usePilotOnboardingStartModalViewModel = ({
    currentOrganization,
    isOpen = true,
    pilotOnboardingService,
    usersService,
}: UsePilotOnboardingStartModalViewModelProps) => {
    const [organizationID, setOrganizationID] = useState('');
    const [countryCode, setCountryCode] = useState('AR');
    const [organizationSetup, setOrganizationSetup] = useState<PilotOrganizationSetup>('presenteeism');
    const [pilotSource, setPilotSource] = useState<PilotSourceOption | ''>('');
    const [name, setName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminName, setAdminName] = useState('');
    const [buildingName, setBuildingName] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmissionResult, setLatestSubmissionResult] = useState<PilotOnboardingSubmissionResult | undefined>(undefined);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setOrganizationID('');
        setCountryCode(currentOrganization?.id === 'DEMO1' ? 'AR' : 'AR');
        setOrganizationSetup('presenteeism');
        setPilotSource('');
        setName('');
        setAdminEmail('');
        setAdminName('');
        setBuildingName('');
        setLatitude('');
        setLongitude('');
        setLatestSubmissionResult(undefined);
    }, [isOpen, currentOrganization?.id]);

    const submit = async () => {
        if (!pilotSource || !canSubmitPilotOnboarding(organizationID, countryCode, name, adminEmail, buildingName, latitude, longitude, isSubmitting, organizationSetup, pilotSource)) {
            return undefined;
        }

        setLatestSubmissionResult(undefined);
        setIsSubmitting(true);

        try {
            const message = await pilotOnboardingService.createPilot({
                organizationID,
                countryCode,
                name,
                adminEmail,
                adminName,
                buildingName,
                latitude,
                longitude,
                organizationSetup,
                pilotSource,
            });

            // if (adminEmail.trim()) {
            //     await usersService.inviteAdminUser(organizationID.trim().toUpperCase(), {
            //         email: adminEmail.trim(),
            //         name: adminName.trim() || adminEmail.trim(),
            //     });
            // }

            const result = { kind: 'success' as const, message };
            setLatestSubmissionResult(result);
            return result;
        } catch (error) {
            const result = {
                kind: 'error' as const,
                errorMessage: getPilotOnboardingErrorMessage(error),
            };
            setLatestSubmissionResult(result);
            return result;
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        organizationID,
        setOrganizationID,
        countryCode,
        setCountryCode,
        organizationSetup,
        setOrganizationSetup,
        pilotSource,
        setPilotSource,
        includesQontactPoint: organizationSetup !== 'payslips',
        name,
        setName,
        adminEmail,
        setAdminEmail,
        adminName,
        setAdminName,
        buildingName,
        setBuildingName,
        latitude,
        setLatitude,
        longitude,
        setLongitude,
        isSubmitting,
        latestSubmissionResult,
        submit,
        canSubmit: canSubmitPilotOnboarding(organizationID, countryCode, name, adminEmail, buildingName, latitude, longitude, isSubmitting, organizationSetup, pilotSource),
    };
};
